import React, { useEffect, useState } from "react";
import {
	Button,
	Label,
	Card,
	Col,
	CardBody,
	CardHeader,
	CardTitle,
	Row,
	Form,
} from "reactstrap";
import InputElement, {
	CheckBoxElement,
	CurrencyInput,
	PercentInput,
	SelectorElement,
} from "../features/forms/components/InputElement";
import { saveSuccessAlert } from "../utils/alertUtils";
import formNavigationUtils from "utils/formNavigationUtils";
import useCurrentAccountStore from "./AccountStore";
import shallow from "zustand/shallow";
import AccountHook from "./AccountHook";
import { AccountStatus } from "../constants/Constants";
import AccountDeleteButton from "./AccountDeleteButton";
import { reCalculateAccount, updateAcctByCloseDate } from "./AccountAPI";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { useFromLocations } from "../stores/LocalStorageHelper";
import PostponeButton from "./PostponeButton";
import { getNotesArray } from "../features/notes/NoteFunctions";
/**
 *
 */
const AccountSettings = () => {
	const dealerState = useFromLocations("state");
	const today = dayjs().format("YYYY-MM-DD");
	const { account, editAccount } = useCurrentAccountStore(
		(state) => state,
		shallow
	);
	const save = AccountHook();

	// Hooks for Account Setting store values
	const [latePercent, setLatePercent] = useState(account.lateChargePct);
	const [lateFixAmount, setLateFixAmount] = useState(account.lateChargeAmt);
	const [lateType, setLateType] = useState(account.lateChargeType);
	const [status, setStatus] = useState(account.status);
	const [subStatus, setSubStatus] = useState(account.subStatus);
	const [closeDate, setCloseDate] = useState(account.closeDate || today);
	const [options, setOptions] = useState([]);
	const [autoMessaging, setAutoMessaging] = useState(
		account?.autoMessaging || false
	);

	// Update options whenever status changes
	useEffect(() => {
		const filteredOptions = AccountStatus.filter(
			(sub) => sub.main === Number(status)
		).map((sub) => ({
			value: sub.label, // Assign value as label if you need
			label: sub.label,
		}));
		setOptions(filteredOptions);
	}, [status]);
	const commandsave = async () => {
		// (1) Copy your form values into the `account` object
		account.lateChargePct = latePercent;
		account.lateChargeAmt = lateFixAmount;
		account.lateChargeType = lateType;
		account.status = status; // note: `status` is a string like "0" or "1"
		account.subStatus = subStatus;
		account.autoMessaging = autoMessaging;

		if (parseInt(status, 10) === 1) {
			account.closeDate = closeDate;
		} else {
			account.closeDate = null;
		}

		// (2) Persist these changes and call save()
		editAccount(account);

		// (3) Only show the warning-dialog if status===1 AND subStatus!=="Paid Off"
		if (
			parseInt(account.status, 10) === 1 &&
			account.subStatus !== "Paid Off"
		) {
			try {
				const res = await updateAcctByCloseDate(account.id);
				// Update the store with the recalculated account
				editAccount(res.data.content);
				await save();
				saveSuccessAlert();
			} catch (err) {
				console.error("Error updating account by close date:", err);
				// Optionally show an error alert here
			}
		} else {
			await save();
			saveSuccessAlert();
		}
	};
	return (
		<div onKeyDown={formNavigationUtils}>
			<Form>
				<Card>
					<CardHeader>
						<CardTitle tag="h3">Late Charge setting</CardTitle>
						{dealerState === "TX" && (
							<Label style={{ color: "blue", fontSize: "14px" }}>
								<h6>Please Note: </h6>In compliance with Texas OCCC, the late
								charge cannot exceed 5% of the payment or past due, and grace
								period cannot be less than 15 days.
							</Label>
						)}
					</CardHeader>
					<CardBody>
						<Col>
							<Row className="mx-0 pb-2">
								<SelectorElement
									name="lateType"
									label="Late Charge by"
									colSize="col-sm-3"
									value={lateType}
									onChange={(e) => setLateType(e.target.value)}
									options={[
										{ value: -1, label: "No Auto Calculation" },
										{ value: 0, label: "Pct of Pmt" },
										{ value: 2, label: "Pct of Past Due" },
										{ value: 1, label: "Flat Fee" },
									]}
									disableValidation
								/>
								<PercentInput
									label="Late Percentage"
									value={latePercent}
									onChange={setLatePercent}
									disableValidation
									colSize="col-sm-3"
								/>
								<Label> + </Label>
								<CurrencyInput
									value={lateFixAmount}
									onChange={setLateFixAmount}
									name="lateFixAmount"
									label="Fixed Amount"
									type="Currency"
									colSize="col-sm-3"
									disableValidation
								/>
							</Row>
						</Col>
					</CardBody>
					<CardHeader>
						<CardTitle tag="h3">Account Status</CardTitle>
					</CardHeader>

					<CardBody>
						<Col>
							<Row className="mx-0 pb-2">
								<SelectorElement
									name="status"
									label="Account Status"
									colSize="col-sm-3"
									value={status}
									onChange={(e) => setStatus(e.target.value)}
									options={[
										{ value: 0, label: "Active" },
										{ value: 1, label: "Inactive" },
									]}
									disableValidation
								/>
								<SelectorElement
									name="subStatus"
									label="Account subStatus"
									colSize="col-sm-3"
									value={subStatus}
									onChange={(e) => setSubStatus(e.target.value)}
									options={options}
									disableValidation
								/>
								{parseInt(status) === 1 && (
									<InputElement
										disableValidation
										value={closeDate}
										name="closeDate"
										label="Close Date"
										type="date"
										onChange={(e) => {
											setCloseDate(e.target.value);
										}}
									/>
								)}

								<Col className="text-center">
									<CheckBoxElement
										disableValidation
										checked={autoMessaging}
										onChange={() => {
											setAutoMessaging(!autoMessaging);
										}}
										name={autoMessaging}
										label="Auto Messaging"
										//colSize="col-sm-3"
										labelClass="pl-4 "
									/>
								</Col>
								<Button color="success" size="lg" onClick={commandsave}>
									<i className="nc-icon nc-cloud-download-93"></i> Save
								</Button>
							</Row>
						</Col>
					</CardBody>
				</Card>
				<Card>
					<CardHeader tag="h3">Actions</CardHeader>
					<CardBody>
						{dealerState !== "TX" && <PostponeButton />}
						<AccountDeleteButton />
						<button
							className="btn btn-primary"
							onClick={(e) => {
								e.preventDefault();
								reCalculateAccount(account.id).then((res) => {
									const account = {
										...res.data.content,
										notes: getNotesArray(res.data.content.notes),
									};
									editAccount(account);
									Swal.close();
									saveSuccessAlert();
								});
							}}
						>
							Recalculate
						</button>
					</CardBody>
				</Card>
			</Form>
		</div>
	);
};

export default AccountSettings;
